import { SearchOutlined } from '@ant-design/icons';
import { Button, Input, type InputRef, theme } from 'antd';
import { type ColumnType, type FilterDropdownProps } from 'antd/lib/table/interface';
import { useEffect, useRef } from 'react';
import styled from 'styled-components';

// getDesignToken() is a plain function (not a hook), so it can be read at
// module scope to keep colours in step with antd across upgrades instead
// of hardcoding the hex. It resolves the *default* theme, which is what
// this app uses - it would not follow a <ConfigProvider theme> if one
// were ever added at the root.
const { colorPrimary } = theme.getDesignToken();

const Container = styled.div`
  padding: 8px;
`;

const SearchInput = styled(Input)`
  display: block;
  margin-bottom: 8px;
  width: 188px;
`;

const ApplyFilterButton = styled(Button).attrs({
  size: 'small',
  type: 'primary'
})`
  margin-right: 8px;
  width: 90px;
`;

const ResetFiltersButton = styled(Button).attrs({
  size: 'small'
})`
  width: 90px;
`;

interface SearchIconProps {
  $active: boolean;
}

const SearchIcon = styled(SearchOutlined)<SearchIconProps>`
  color: ${(p) => (p.$active ? colorPrimary : 'inherit')};
`;

export function SearchFilterDropdown({
  clearFilters,
  confirm,
  selectedKeys,
  setSelectedKeys,
  visible
}: FilterDropdownProps) {
  const inputRef = useRef<InputRef>(null);

  // Focus and select the input whenever the dropdown opens.
  // rAF is necessary.
  useEffect(() => {
    if (!visible) return;
    const raf = requestAnimationFrame(() => inputRef.current?.select());
    return () => cancelAnimationFrame(raf);
  }, [visible]);

  return (
    <Container>
      <SearchInput
        onChange={(e) =>
          setSelectedKeys?.(e.target.value ? [e.target.value] : [])
        }
        onPressEnter={() => confirm()}
        placeholder="Search"
        ref={inputRef}
        value={String(selectedKeys?.[0] || '')}
      />
      <ApplyFilterButton icon={<SearchOutlined />} onClick={() => confirm()}>
        Search
      </ApplyFilterButton>
      <ResetFiltersButton
        onClick={() => {
          clearFilters?.();
          confirm();
        }}
      >
        Reset
      </ResetFiltersButton>
    </Container>
  );
}

/**
 * The fixed search-filter UI (dropdown + active-state icon) shared by every
 * searchable column. Spread into a ColumnType and pair with an `onFilter`
 * (e.g. makeStringFilter). These fields carry no per-column state, so this is
 * a constant rather than a factory.
 */
export const searchFilterFields: Pick<
  ColumnType<unknown>,
  'filterDropdown' | 'filterIcon'
> = {
  filterDropdown: (props) => <SearchFilterDropdown {...props} />,
  filterIcon: (filtered) => <SearchIcon $active={filtered} />,
};
